import { CircleHelp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  sanitizeCascadeRemindersInput,
  sanitizeOffsetRemindersInput,
  sanitizeReminderDurationInput,
} from './assignmentTimeline'

type Props = {
  cascadeValue: string
  offsetValue: string
  durationValue: string
  cascadePlaceholder?: string
  offsetPlaceholder?: string
  durationPlaceholder?: string
  assignmentGroups?: number
  canEdit: boolean
  onCascadeChange: (value: string) => void
  onOffsetChange: (value: string) => void
  onDurationChange: (value: string) => void
  onBlur: () => void
}

const FieldLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div className='flex h-5 items-center gap-1 whitespace-nowrap'>
    <Label>{label}</Label>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex shrink-0 items-center text-muted-foreground'>
          <CircleHelp className='h-3.5 w-3.5' />
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-72'>{tooltip}</TooltipContent>
    </Tooltip>
  </div>
)

const AssignmentReminderTimingFields = ({
  cascadeValue,
  offsetValue,
  durationValue,
  cascadePlaceholder,
  offsetPlaceholder,
  durationPlaceholder,
  assignmentGroups = 1,
  canEdit,
  onCascadeChange,
  onOffsetChange,
  onDurationChange,
  onBlur,
}: Props) => {
  const { t } = useTranslation()

  return (
    <>
      <div className='flex w-28 shrink-0 flex-col justify-end'>
        <FieldLabel
          label={t('dashboard.team.plans.assignments.offset-reminders-by')}
          tooltip={t(
            'dashboard.team.plans.assignments.offset-reminders-by-tooltip',
          )}
        />
        <Input
          type='text'
          inputMode='decimal'
          maxLength={6}
          value={offsetValue}
          onChange={(event) =>
            onOffsetChange(sanitizeOffsetRemindersInput(event.target.value))
          }
          onBlur={onBlur}
          className='h-8'
          placeholder={offsetPlaceholder}
          disabled={!canEdit}
        />
      </div>
      <div className='flex w-28 shrink-0 flex-col justify-end'>
        <FieldLabel
          label={t('dashboard.team.plans.assignments.reminder-duration')}
          tooltip={t(
            'dashboard.team.plans.assignments.reminder-duration-tooltip',
          )}
        />
        <Input
          type='text'
          inputMode='numeric'
          maxLength={3}
          value={durationValue}
          onChange={(event) =>
            onDurationChange(sanitizeReminderDurationInput(event.target.value))
          }
          onBlur={onBlur}
          className='h-8'
          placeholder={durationPlaceholder}
          disabled={!canEdit}
        />
      </div>
      <div className='flex w-28 shrink-0 flex-col justify-end'>
        <FieldLabel
          label={t('dashboard.team.plans.assignments.cascade-reminders-by')}
          tooltip={t(
            assignmentGroups > 1
              ? 'dashboard.team.plans.assignments.cascade-reminders-by-groups-tooltip'
              : 'dashboard.team.plans.assignments.cascade-reminders-by-tooltip',
          )}
        />
        <Input
          type='text'
          inputMode='decimal'
          maxLength={4}
          value={cascadeValue}
          onChange={(event) =>
            onCascadeChange(sanitizeCascadeRemindersInput(event.target.value))
          }
          onBlur={onBlur}
          className='h-8'
          placeholder={cascadePlaceholder}
          disabled={!canEdit}
        />
      </div>
    </>
  )
}

export default AssignmentReminderTimingFields
